import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../store";
import { addToast } from "../store/slices/uiSlice";
import {
  getWalletBalances,
  getTransactions,
  deposit,
  withdraw,
  createTransaction,
} from "../mocks/wallets";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useWalletData = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = user?.id;

  const balancesQuery = useQuery({
    queryKey: ["wallet", userId, "balances"],
    queryFn: async () => {
      if (!userId) return [];
      await delay(500);
      return getWalletBalances(userId);
    },
    enabled: !!userId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const transactionsQuery = useQuery({
    queryKey: ["wallet", userId, "transactions"],
    queryFn: async () => {
      if (!userId) return [];
      await delay(500);
      return getTransactions(userId);
    },
    enabled: !!userId,
    refetchInterval: 30000,
  });

  const totalBalance =
    balancesQuery.data?.reduce(
      (sum, b) => sum + (b.available + b.locked) * b.priceUsd,
      0,
    ) || 0;
  const totalChange24h =
    balancesQuery.data?.reduce((sum, b) => {
      const value = (b.available + b.locked) * b.priceUsd;
      return sum + (value * b.change24h) / 100;
    }, 0) || 0;

  return {
    balances: balancesQuery.data || [],
    transactions: transactionsQuery.data || [],
    totalBalance,
    totalChange24h,
    isLoading: balancesQuery.isLoading || transactionsQuery.isLoading,
    refetch: () => {
      balancesQuery.refetch();
      transactionsQuery.refetch();
    },
  };
};

export const useDeposit = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  return useMutation({
    mutationFn: async ({
      asset,
      amount,
    }: {
      asset: string;
      amount: number;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");
      await delay(1500);
      return deposit(user.id, asset, amount);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["wallet", user?.id] });
      dispatch(
        addToast({
          type: "success",
          message: `Successfully deposited ${variables.amount} ${variables.asset}`,
        }),
      );
    },
    onError: (error: Error) => {
      dispatch(addToast({ type: "error", message: error.message }));
    },
  });
};

export const useWithdraw = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  return useMutation({
    mutationFn: async ({
      asset,
      amount,
      address: _address,
    }: {
      asset: string;
      amount: number;
      address: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");
      await delay(1500);
      const fee = amount * 0.001; // 0.1% fee
      return withdraw(user.id, asset, amount, fee);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["wallet", user?.id] });
      dispatch(
        addToast({
          type: "success",
          message: `Successfully withdrew ${variables.amount} ${variables.asset}`,
        }),
      );
    },
    onError: (error: Error) => {
      dispatch(addToast({ type: "error", message: error.message }));
    },
  });
};

export const useTransfer = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  return useMutation({
    mutationFn: async ({
      asset,
      amount,
      recipientId,
    }: {
      asset: string;
      amount: number;
      recipientId: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");
      await delay(1000);

      // Deduct from sender
      const senderBalance = getWalletBalances(user.id).find(
        (b) => b.asset === asset,
      );
      if (!senderBalance || senderBalance.available < amount) {
        throw new Error("Insufficient balance");
      }

      // Create transaction
      createTransaction({
        userId: user.id,
        type: "transfer",
        asset,
        amount: -amount,
        status: "completed",
        toAddress: recipientId,
        description: `Transfer ${amount} ${asset} to user ${recipientId}`,
      });

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallet", user?.id] });
      dispatch(
        addToast({
          type: "success",
          message: "Transfer completed successfully",
        }),
      );
    },
    onError: (error: Error) => {
      dispatch(addToast({ type: "error", message: error.message }));
    },
  });
};

// Admin hooks
export const useAdminWallets = () => {
  return useQuery({
    queryKey: ["admin", "wallets"],
    queryFn: async () => {
      await delay(600);
      return {
        totalAssets: "$45.2M",
        deposits24h: "$2.1M",
        withdrawals24h: "$1.8M",
        activeWallets: 8432,
      };
    },
  });
};
